import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { merge } from 'lodash';
import moment from 'moment';
import { fromEvent, Observable, ReplaySubject, Subject, debounceTime } from 'rxjs';
import { DvrControlsPlaybackState, DvrControlsState } from '../models/dvr.model';
import { BadgeEvent } from '@weavix/models/src/badges/event';
import { BadgeUpdate } from '@weavix/models/src/badges/badge-update';
import { Facility } from '../models/facility.model';
import { Item } from '../models/item.model';
import { Person } from '../models/person.model';
import { Topic } from '@weavix/models/src/topic/topic';
import { DvrItem, DvrItems, DvrPeople, DvrPerson, DvrState, Geofence, MapCacheObject } from '../models/weavix-map.model';
import { CSS_BREAKPOINTS } from '../utils/css-breakpoints';
import { hashLocation } from '../utils/polygon';
import { Utils } from '../utils/utils';
import { AccountService } from './account.service';
import { AlertService } from './alert.service';
import { BadgeService } from './badge.service';
import { FacilityService } from './facility.service';
import { ItemService } from './item.service';
import { NewRelicService } from './new-relic.service';
import { PersonService } from './person.service';
import { PubSubService } from './pub-sub.service';
import { TranslationService } from './translation.service';

export interface MapItemEvent {
    [id: string]: Item;
}

export interface MapGeofenceEvent {
    [id: string]: Geofence;
}

export interface BadgeRegistrationEvent {
    badgeName?: string;
}

export interface BadgeRegistrationPayload extends BadgeRegistrationEvent {
    badgeName: string;
    deregistered: boolean;
}

export interface BadgePayload {
    personId?: string;
    event?: BadgeUpdate;
    registration?: BadgeRegistrationPayload;
}

export enum DvrEntities {
    People = 'people',
    Items = 'items',
}

export const MAP_UPDATE_INTERVAL: number = 10_000;

@Injectable({
    providedIn: 'root',
})
export class MapService {
    private readonly mapTranslationKeys: string[] = [
        'generics.edit',
        'generics.moreInfo',
        'name',
        'area',
        'company.company',
        'people',
        'items.items',
        'generics.wifi-routers',
        'beacons',
    ];
    private queryParamFilters: { [key: string]: string } = {};
    public dvrPlaybackState: DvrControlsPlaybackState = {
        inPlaybackMode: false,
        timestamp: null,
    };
    private dvrControlsState: DvrControlsState = {
        togglePlaybackMode: false,
        range: { from: moment().startOf('day').toDate(), to: moment().endOf('day').toDate() },
        timestamp: Date.now(),
        inPlaybackMode: false,
        firstLoad: false,
    };
    public dvrDataState: DvrState;
    /** The window being loaded; live badge updates feed it too, so none are lost when it replaces `dvrDataState`. */
    private loadingDvrDataState?: DvrState;
    /** The load in progress, its `account/site` and the component it is for; the same component asking for the same scope waits for it instead of fetching again. */
    private dvrLoad?: { scope: string, component: unknown, done: Promise<unknown> };
    public dvrSliderDrop$ = new Subject<any>();
    public dvrPlaybackState$: Subject<DvrControlsPlaybackState> = new ReplaySubject(1);

    public dashboard: boolean = true;
    public dashboard$ = new ReplaySubject<boolean>(1);
    private mapUpdateInterval: number;
    public mapUpdateIntervalTrigger = new Subject<void>();

    private subscribedToBadges;
    private prevWindowSize: number;
    public routeParamChange$ = new Subject<{ [key: string]: string }>();

    private lastDvrTimestamp: number | undefined;

    constructor(
        public translationService: TranslationService,
        private readonly facilityService: FacilityService,
        private readonly peopleService: PersonService,
        private readonly badgeService: BadgeService,
        private readonly pubSubService: PubSubService,
        private readonly accountService: AccountService,
        private readonly alertService: AlertService,
        private readonly route: ActivatedRoute,
        private readonly itemService: ItemService,
    ) {
        if (this.route.queryParams) {
            this.route.queryParams.subscribe(params => this.setRouteFilters(params));
        }
    }

    startMapUpdateInterval(): void {
        if (!this.mapUpdateInterval) {
            this.mapUpdateInterval = window.setInterval(() => {
                this.mapUpdateIntervalTrigger.next();
            }, MAP_UPDATE_INTERVAL);
        }
    }

    stopMapUpdateInterval(): void {
        if (this.mapUpdateInterval && !this.mapUpdateIntervalTrigger.observers.length) clearInterval(this.mapUpdateInterval);
    }

    async subscribeBadgeUpdates(component: any) {
        const accountId = this.accountService.getAccountId();
        const facility = this.facilityService.getCurrentFacility();
        if (this.subscribedToBadges === (facility?.id ?? accountId)) return;
        this.subscribedToBadges = facility?.id ?? accountId;
        (await this.pubSubService.subscribe<BadgeEvent>(component, facility ? Topic.AccountFacilityPersonBadgeUpdated
            : Topic.AccountPersonBadgeUpdated, facility ? [accountId, facility.id, '+'] : [accountId, '+']))
            .subscribe((message) => {
                if (message.payload.location) {
                    this.dvrWindows(accountId, facility?.id ?? null).forEach(state => this.recordLiveBadge(state, message.replacements[1], message.payload));
                }
            });
        (await this.pubSubService.subscribe<BadgeRegistrationEvent>(component, Topic.AccountPersonBadgeDeregistered, [accountId, '+']))
            .subscribe((message) => {
                this.dvrWindows(accountId).forEach(state => {
                    const person = state.people[message.replacements[1]];
                    if (person?.latestEvent) delete person.latestEvent.name;
                });
            });
    }

    /** The visible DVR window and the one loading, without duplicates, limited to an account and, unless `facilityId` is undefined, a site (`null` for none). */
    private dvrWindows(accountId: string, facilityId?: string | null): DvrState[] {
        return [...new Set([this.dvrDataState, this.loadingDvrDataState])]
            .filter(state => state?.accountId === accountId && (facilityId === undefined || (state.facility?.id ?? null) === facilityId));
    }

    /** Adds a live badge update to a DVR window: as an event when it falls inside the window, and as the person's latest. */
    private recordLiveBadge(state: DvrState, personId: string, badge: BadgeEvent): void {
        const time = new Date(badge.date).getTime();
        if (time >= state.range.from.getTime() && time < state.range.to.getTime()) {
            state.events[personId] = state.events[personId] || [];
            state.events[personId].push(badge as any);
        }

        const person = state.people[personId];
        if (person) {
            person.latestEvent = badge;
            if (!this.dvrPlaybackMode) person.badge = badge as any;
        }
    }

    set dashboardMode(value: boolean) {
        this.dashboard = value;
        this.dashboard$.next(value);
    }

    get dashboardMode() {
        return this.dashboard;
    }

    get dvrActiveTimeRange(): { from: number, to: number } {
        return { from: this.dvrControlsState.range.from.getTime(), to: this.dvrPlaybackMode ? this.dvrTimestamp : this.dvrControlsState.range.to.getTime() };
    }

    get dvrState() {
        return this.dvrControlsState;
    }

    get dvrTimestamp() {
        // dvr timestamp is used to compare with the current date. Default to current time if none set
        return (this.dvrPlaybackState && this.dvrPlaybackState.timestamp) || Date.now();
    }

    set dvrTimestamp(timestamp: number) {
        this.dvrPlaybackState.timestamp = timestamp;
    }

    get dvrPlaybackMode() {
        return this.dvrPlaybackState && this.dvrPlaybackState.inPlaybackMode;
    }

    set dvrPlaybackMode(inPlaybackMode: boolean) {
        this.dvrPlaybackState.inPlaybackMode = inPlaybackMode;
    }

    public clearPlaybackState(): void {
        this.dvrPlaybackState = null;
    }


    /**
     * Loads a DVR window covering `timestamp` unless the visible one already does. A window for another account or site is hidden at once; a
     * same-site window stays until the new one has fully loaded, and is kept if that load fails. Failures are rethrown.
     */
    async initDvrRange(component: unknown, _from: Date, _to: Date, timestamp: number, entities: DvrEntities[], facilityId?: string) {
        const accountId = this.accountService.getAccountId();
        this.lastDvrTimestamp = undefined;
        const scope = `${accountId}/${facilityId ?? ''}`;
        let inFlight = this.dvrLoad;
        while (inFlight?.scope === scope && inFlight.component === component) {
            await inFlight.done.catch(() => undefined);
            if (this.dvrLoad === inFlight) this.dvrLoad = undefined;
            inFlight = this.dvrLoad;
        }
        const active = this.dvrDataState;
        const sameScope = active?.accountId === accountId && active?.facility?.id === facilityId;
        if (sameScope && this.coversDvrTimestamp(timestamp)) return this;
        if (!sameScope) this.dvrDataState = undefined;

        const done = this.swapInDvrWindow(component, timestamp, entities, accountId, facilityId);
        this.dvrLoad = { scope, component, done };
        try {
            await done;
        } finally {
            if (this.dvrLoad?.done === done) this.dvrLoad = undefined;
        }
        return this;
    }

    /** Loads a window and swaps it in on success; on failure the current window stays and the error is rethrown. */
    private async swapInDvrWindow(component: unknown, timestamp: number, entities: DvrEntities[], accountId: string, facilityId: string | undefined): Promise<void> {
        this.dvrDataState = await this.alertService.withLoading(this.loadDvrWindow(component, timestamp, entities, accountId, facilityId));
        this.lastDvrTimestamp = undefined;
    }

    /** Whether the visible window has the site's lookback before `timestamp` and a minute of buffer after it. */
    coversDvrTimestamp(timestamp: number): boolean {
        const state = this.dvrDataState;
        if (!state) return false;
        const lookbackMs = (state.facility?.settings?.locationTimeoutHrs ?? 1.25) * 3_600_000;
        return state.range.from.getTime() <= timestamp - lookbackMs && state.range.to.getTime() >= timestamp + 60_000;
    }

    /** Loads a new window around `timestamp`, reaching back at least 90 minutes or the site's location timeout if longer. */
    private async loadDvrWindow(component: unknown, timestamp: number, entities: DvrEntities[], accountId: string, facilityId?: string): Promise<DvrState> {
        const facility = facilityId ? await this.facilityService.get(component, facilityId, true) : null;
        const lookbackMs = Math.max(90 * 60_000, (facility?.settings?.locationTimeoutHrs ?? 1.25) * 3_600_000);
        const from = new Date(timestamp - lookbackMs);
        const to = new Date(timestamp + 30 * 60_000);
        const state: DvrState = { people: {}, events: {}, facility, range: { from, to }, accountId };
        this.loadingDvrDataState = state;

        const getDvrPeople = async () => {
            state.people = (await this.peopleService.getPeople(component)).reduce((obj: DvrPeople, p: DvrPerson) => (obj[p.id] = p, obj), {});
            const peopleEvents = await this.badgeService.getPeopleEvents(component, facilityId, from, to);
            const dvrEventsMap = peopleEvents.reduce(
                (acc, curr) => {
                    if (curr.personId) {
                        curr.facilities = [{ id: facilityId }];
                        if (curr.personId in acc) acc[curr.personId].push(curr);
                        else acc[curr.personId] = [curr];
                    }
                    return acc;
                },
                {});
            state.events = merge(dvrEventsMap, state.events);
        };

        const getDvrItems = async () => {
            state.items = (await this.itemService.getItems(component, facilityId)).reduce((obj: DvrItems, item: DvrItem) => (obj[item.id] = item, obj), {});
        };

        try {
            for (const e of entities) {
                switch (e) {
                    case DvrEntities.Items :
                        await getDvrItems();
                        break;
                    case DvrEntities.People :
                        await getDvrPeople();
                        break;
                }
            }
        } finally {
            if (this.loadingDvrDataState === state) this.loadingDvrDataState = undefined;
        }
        return state;
    }

    getDvrMinuteDataPeople(timestamp: number): { [key: string]: DvrPerson } {
        if (!timestamp) {
            if (this.dvrDataState) {
                Object.values(this.dvrDataState.people).forEach(p => p.badge = p.latestEvent || p.badge);
                const data = this.dvrDataState.people;
                return data;
            }
            return null;
        }

        if (!this.dvrDataState) return null;
        const locationTimeoutHrs = this.dvrDataState.facility?.settings?.locationTimeoutHrs ?? 1.25;
        const date = this.lastDvrTimestamp ?? (timestamp - locationTimeoutHrs * 3_600_000);
        Object.values(this.dvrDataState.people).forEach(p => {
            const events = this.dvrDataState.events[p.id] || [];
            let min = 0;
            let max = events.length;
            while (min < max - 1) {
                const mid = Math.floor((min + max) / 2);
                if (new Date(events[mid].date).getTime() < date) {
                    min = mid + 1;
                } else {
                    max = mid;
                }
            }
            let latestEvent = this.lastDvrTimestamp ? p.badge : null;
            for (let i = min; i < events.length; i++) {
                const event = events[i];
                if (new Date(event.date).getTime() > timestamp) break;

                if (!latestEvent) latestEvent = {};
                Object.assign(latestEvent, events[i]);
            }

            p.latestEvent = p.latestEvent || p.badge;
            if (!latestEvent?.location) latestEvent = null;
            if (latestEvent) latestEvent.name = p.badge && p.badge.name || 'yes';
            p.badge = latestEvent;
        });

        this.lastDvrTimestamp = timestamp;
        return this.dvrDataState.people;
    }

    getDvrMinuteDataItems(timestamp: number): { [key: string]: DvrItem } {
        if (!timestamp) {
            if (this.dvrDataState) {
                return this.dvrDataState.items;
            }
            return null;
        }

        if (!this.dvrDataState) return null;
        Object.values(this.dvrDataState.items).forEach(item => {
            const events = this.dvrDataState.events[item.id] || [];
            let min = 0;
            let max = events.length;
            while (min < max - 1) {
                const mid = Math.floor((min + max) / 2);
                if (new Date(events[mid].date).getTime() < timestamp) {
                    min = mid;
                } else {
                    max = mid;
                }
            }
            item.latestEvent = Object.assign({}, events[min]);
        });

        return this.dvrDataState.items;
    }

    public setDvrPlaybackState(playbackState: DvrControlsPlaybackState): void {
        this.dvrPlaybackState = playbackState;
        this.dvrPlaybackState$.next(playbackState);
    }

    public setDvrControlsState(controlsState: DvrControlsState) {
        this.dvrControlsState = controlsState;
    }

    getMapTranslations(): Observable<{ [key: string]: string }> & PromiseLike<{ [key: string]: string }> {
        return Utils.awaitable(this.translationService.getTranslations(this.mapTranslationKeys));
    }

    public setRouteFilters(queryParams: any): void {
        this.queryParamFilters = {};
        for (const param in queryParams) {
            if (queryParams[param]) {
                this.queryParamFilters[param] = queryParams[param].toString();
            }
        }

        this.routeParamChange$.next(this.queryParamFilters);
    }

    public getRouteFilters(): { [key: string]: string } {
        return this.queryParamFilters;
    }

    public getRouteFilter(param: string): string {
        return this.queryParamFilters?.[param];
    }

    public setupDashboardDisplay(component): void {
        if (window.innerWidth < CSS_BREAKPOINTS.full) this.dashboardMode = false;
        this.prevWindowSize = window.innerWidth;

        Utils.safeSubscribe(component, fromEvent(window, 'resize')).pipe(debounceTime(250)).subscribe(x => {
            const resizingDown: boolean = this.prevWindowSize > CSS_BREAKPOINTS.full && window.innerWidth < CSS_BREAKPOINTS.full;
            const resizingUp: boolean = this.prevWindowSize < CSS_BREAKPOINTS.full && window.innerWidth > CSS_BREAKPOINTS.full;

            if (resizingDown) {
                this.dashboardMode = false;
            } else if (resizingUp) {
                this.dashboardMode = true;
            }
            this.prevWindowSize = window.innerWidth;
        });
    }

    public async updateHeatMapData(component, cache: MapCacheObject, facility: Facility, filterPoint, people: Map<string, Person>): Promise<{ cache: MapCacheObject, locations: { [key: string]: any[] } }> {
        if (!cache) return;
        const points = cache.heatmap;
        const addPoint = function (point: number[], weight: number) {
            if (weight <= 0 || isNaN(weight)) return;
            const hash = hashLocation(point);
            if (!points[hash.hash]) points[hash.hash] = { location: new google.maps.LatLng(hash.location[1], hash.location[0]), weight };
            else points[hash.hash].weight += weight;
        };
        const startTime: moment.Moment = moment().tz(facility.timezone).startOf('day');
        const endTime: moment.Moment = moment().tz(facility.timezone).endOf('day');
        const filters = cache.filters;
        const locations = {};
        // const filterPoint = ;
        if (!this.dvrDataState || this.dvrDataState.facility?.id !== facility?.id) {
            try {
                await this.initDvrRange(component, startTime.toDate(), endTime.toDate(), this.dvrTimestamp, [DvrEntities.People], facility?.id);
            } catch (e) {
                const ids = { source: 'heat-map-dvr', accountId: this.accountService.getAccountId(), facilityId: facility?.id ?? '' };
                const { message, status } = (e ?? {}) as { message?: unknown, status?: unknown };
                console.error('Heat map DVR load failed', ids, e);
                NewRelicService.sendError(
                    e instanceof Error ? e : new Error(typeof message === 'string' ? message : String(e)),
                    typeof status === 'number' ? { ...ids, status } : ids,
                );
                return { cache, locations };
            }
        }
        if (!this.dvrDataState) return { cache, locations };

        const from = this.dvrDataState.range.from.getTime();
        const to = Math.max(this.dvrTimestamp, this.dvrDataState.range.to.getTime());

        [...people?.keys() || []].forEach(id => {
            const locationEvents = this.dvrDataState.events[id] ?? [];
            let lastEvent;
            let lastEventIndex = cache.points[id] ?? 0;
            for (let i = lastEventIndex; i < locationEvents.length; i++) {
                const event = locationEvents[i];
                if (!event.location) continue;

                if (lastEvent) {
                    const start = new Date(lastEvent.date).getTime();
                    const end = new Date(event.date).getTime();
                    if (end > from && start < to) {
                        const personId = lastEvent.personId;
                        filters[personId] = 1;
                        if (!locations[personId]) locations[personId] = [];
                        locations[personId].push(lastEvent);
                        if (filterPoint(personId, lastEvent.location)) {
                            const duration = Math.min(end, to) - Math.max(start, from);
                            if (duration > 0) addPoint(lastEvent.location, duration / 1000);
                        }
                    }
                }
                lastEventIndex = i;
                lastEvent = event;
            }
            cache.points[id] = lastEventIndex;
        });

        return { cache, locations };
    }
}
